import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const RefundModal = ({ dealId, onClose, onSuccess }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    amount: '',
    refund_date: new Date().toISOString().split('T')[0],
    reason: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid refund amount', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${dealId}/refund`, {
        amount: parseFloat(formData.amount),
        refund_date: formData.refund_date,
        reason: formData.reason,
        notes: formData.notes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: 'Success', description: 'Refund processed successfully' });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to process refund', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">Process Refund</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Refund Amount *</label>
              <Input
                required
                type="number"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                placeholder="1000.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Refund Date *</label>
              <Input
                required
                type="date"
                value={formData.refund_date}
                onChange={(e) => setFormData({...formData, refund_date: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Reason *</label>
            <select
              required
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              className="w-full border rounded p-2"
            >
              <option value="">Select reason...</option>
              <option value="Overpayment">Overpayment</option>
              <option value="Deal Cancellation">Deal Cancellation</option>
              <option value="Error">Processing Error</option>
              <option value="Customer Request">Customer Request</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full border rounded p-2 h-24"
              placeholder="Additional refund details..."
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <p className="text-sm text-yellow-800">
              <strong>Warning:</strong> This will create a refund transaction and adjust the deal's collected amount and remaining balance.
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-red-600 hover:bg-red-700">
              {loading ? 'Processing...' : 'Process Refund'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading}>
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RefundModal;
